import { useEffect, useState } from 'react';
import { fetchReportDetail, type MatchDetail } from '../api/reports';
import { ca } from '../i18n/ca';
import { AssetPicture } from './AssetPicture';

/**
 * What was fired, what was driven, what was asked of the night and who fell,
 * under a report's row.
 *
 * The panel has counted every weapon's shots and kills, every vehicle's time
 * and distance, and every vehicle type that was lost, since August — and this
 * page showed four numbers and a link out to it. Nothing is recomputed here and
 * nothing new is extracted: the rows come from the panel's own report, and their
 * icons come from `/api/catalog/picture`, which asks the panel too.
 *
 * Loaded when somebody opens the row and not before. A season of reports is a
 * long table, and asking the panel about every match in it to render a page
 * nobody has expanded would be one request per night on the list.
 */
const t = ca.reportsPage2;
/** How much of the feed a row shows. The whole of it is the panel's own page. */
const FEED_SHOWN = 40;

/** The loader the reports page uses: one report, fetched when its row is opened. */
export function MatchArmament({ id }: { id: number }) {
  const [detail, setDetail] = useState<MatchDetail | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'none'>('loading');

  useEffect(() => {
    let live = true;
    setState('loading');
    fetchReportDetail(id)
      .then((d) => {
        if (!live) return;
        setDetail(d);
        setState('ready');
      })
      .catch(() => live && setState('none'));
    return () => {
      live = false;
    };
  }, [id]);

  if (state === 'loading') {
    return (
      <p className="muted small" role="status">
        {t.detailLoading}
      </p>
    );
  }
  if (state === 'none' || !detail) {
    return <p className="muted small">{t.detailNone}</p>;
  }
  return <MatchTables detail={detail} />;
}

/**
 * The tables themselves, from a detail somebody else fetched. The session page
 * asks for its own and draws the heat map beside them; the reports page has one
 * row per night and loads them one at a time.
 */
export function MatchTables({ detail }: { detail: MatchDetail }) {
  /** The last of the feed: a long night is thousands, and this is a table row. */
  const feed = detail.killFeed.slice(-FEED_SHOWN).reverse();

  return (
    <div className="armament">
      {detail.tasks.length > 0 && (
        <section>
          <h4>{t.tasks}</h4>
          <ul className="task-list">
            {detail.tasks.map((task, i) => (
              <li key={`${task.title}-${i}`} className={`task ${task.state.toLowerCase()}`}>
                <i
                  className={`bi ${
                    task.state === 'SUCCEEDED'
                      ? 'bi-check-circle-fill'
                      : task.state === 'FAILED'
                        ? 'bi-x-circle-fill'
                        : task.state === 'CANCELED'
                          ? 'bi-slash-circle'
                          : 'bi-circle'
                  }`}
                  aria-hidden="true"
                />
                <span className="grow">{task.title}</span>
                <span className="chip">{t.taskStates[task.state] ?? task.state}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {detail.weapons.length > 0 && (
        <section>
          <h4>{t.weapons}</h4>
          <table className="rows">
            <thead>
              <tr>
                <th scope="col">{t.colWeapon}</th>
                <th scope="col" className="num">
                  {t.colShots}
                </th>
                <th scope="col" className="num">
                  {t.colWeaponKills}
                </th>
                <th scope="col">{t.colUsers}</th>
              </tr>
            </thead>
            <tbody>
              {detail.weapons.map((w) => (
                <tr key={w.className}>
                  <th scope="row">
                    <AssetPicture
                      size={28}
                      kind="weapon"
                      assetClass={w.className}
                      name={w.className}
                    />
                    <span>{w.name}</span>
                  </th>
                  <td className="num">{w.shots}</td>
                  <td className="num">{w.kills}</td>
                  <td className="muted small">
                    {w.users
                      .slice(0, 3)
                      .map((u) => u.name)
                      .join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {detail.vehicles.length > 0 && (
        <section>
          <h4>{t.vehicles}</h4>
          <table className="rows">
            <thead>
              <tr>
                <th scope="col">{t.colVehicle}</th>
                <th scope="col" className="num">
                  {t.colTime}
                </th>
                <th scope="col" className="num">
                  {t.colDistance}
                </th>
                <th scope="col" className="num">
                  {t.colWeaponKills}
                </th>
              </tr>
            </thead>
            <tbody>
              {detail.vehicles.map((v) => (
                <tr key={v.className}>
                  <th scope="row">
                    <AssetPicture
                      size={40}
                      kind="vehicle"
                      assetClass={v.className}
                      name={v.className}
                    />
                    <span>{v.name}</span>
                  </th>
                  <td className="num">{t.minutes(Math.round(v.secondsUsed / 60))}</td>
                  <td className="num">{t.kilometres(v.distanceMeters)}</td>
                  <td className="num">{v.kills}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {detail.destroyed.length > 0 && (
        <section>
          <h4>{t.destroyed}</h4>
          <table className="rows">
            <thead>
              <tr>
                <th scope="col">{t.colVehicle}</th>
                <th scope="col" className="num">
                  {t.colDestroyed}
                </th>
                <th scope="col" className="num">
                  {t.colFriendly}
                </th>
                <th scope="col">{t.colDestroyers}</th>
              </tr>
            </thead>
            <tbody>
              {detail.destroyed.map((v) => (
                <tr key={`${v.className}-${v.side}`}>
                  <th scope="row">
                    <AssetPicture
                      size={40}
                      kind="vehicle"
                      assetClass={v.className}
                      name={v.className}
                    />
                    <span>{v.name}</span>
                    {v.side && (
                      <span className={`chip side-${v.side.toLowerCase()}`}>{v.side}</span>
                    )}
                  </th>
                  <td className="num">{v.destroyed}</td>
                  <td className={`num ${v.friendlyLosses > 0 ? 'warn' : ''}`}>
                    {v.friendlyLosses}
                  </td>
                  <td className="muted small">
                    {v.destroyers.length ? v.destroyers.slice(0, 3).join(', ') : t.byAi}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {feed.length > 0 && (
        <section className="feed">
          <h4>
            {t.killFeed}{' '}
            {detail.killFeed.length > FEED_SHOWN && (
              <span className="muted small">{t.killFeedOf(FEED_SHOWN)}</span>
            )}
          </h4>
          <ul className="kill-feed">
            {feed.map((k, i) => (
              <li key={`${k.at}-${i}`} className={k.friendlyFire ? 'friendly' : undefined}>
                <span className="who">{k.suicide ? k.victim : k.killer || t.byAi}</span>
                <AssetPicture
                  size={22}
                  kind="weapon"
                  assetClass={k.weaponClass}
                  name={k.weaponName}
                />
                <span className="grow">{k.victim}</span>
                {k.distance > 0 && <span className="muted small">{t.metres(k.distance)}</span>}
                {k.friendlyFire && <span className="chip warn">{t.friendlyFire}</span>}
                {k.suicide && <span className="chip">{t.suicide}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
